import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { get, post, put, del, postFiles } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";
import Layout from "../../layout/Main/Layout";

const CbtTasks = () => {
  // State management
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalTasks, setTotalTasks] = useState(0);

  // Dialog states
  const [taskDialog, setTaskDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    sessionNo: "0", // Default to session 0
    sessionType: "",
    search: "",
  });
  const [expandedSessions, setExpandedSessions] = useState({});

  // Form states
  const [formData, setFormData] = useState({
    sessionNo: "",
    sessionType: "",
    title: "",
    description: "",
    icon: "",
    iconFile: null, // For file upload
    frequency: "One-time",
    dueInterval: 1,
    reminders: {
      enabled: false,
      time: "",
      days: [],
    },
    order: 0,
  });

  // Constants
  const sessionTypes = ["DISCOVERY", "ANALYSIS", "SOLUTION"];
  const frequencies = ["Daily", "Weekly", "Monthly", "One-time"];
  const daysOfWeek = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
  ];

  // Effects
  useEffect(() => {
    fetchTasks();
  }, [page, rowsPerPage, filters]);

  // API Functions
  const fetchTasks = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: 100, // Get more tasks to show all sessions
        search: filters.search,
      });

      const response = await get(`/api/dashboard/cbt/cbt-tasks?${queryParams}`);
      console.log("Fetched tasks response:", response);
      if (response?.success) {
        console.log("Tasks data:", response.data);
        console.log("Tasks with icon:", response.data.filter(task => task.icon));
        setTasks(response.data);
        setTotalTasks(response.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toastMessage("Error fetching tasks", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleAdd = () => {
    setFormData({
      sessionNo: filters.sessionNo.toString(), // Use current selected session
      sessionType: "DISCOVERY", // Default to DISCOVERY instead of empty string
      title: "",
      description: "",
      icon: "",
      iconFile: null, // Reset icon file
      frequency: "One-time",
      dueInterval: 1,
      reminders: {
        enabled: false,
        time: "",
        days: [],
      },
      order: 0,
    });
    setIsEditing(false);
    setSelectedTask(null);
    setTaskDialog(true);
  };

  const handleEdit = (task) => {
    setFormData({
      sessionNo: task.sessionNo.toString(),
      sessionType: task.sessionType,
      title: task.title,
      description: task.description,
      icon: task.icon || "",
      iconFile: null, // Reset icon file for editing
      frequency: task.frequency,
      dueInterval: task.dueInterval,
      reminders: task.reminders || { enabled: false, time: "", days: [] },
      order: task.order || 0,
    });
    setIsEditing(true);
    setSelectedTask(task);
    setTaskDialog(true);
  };

  const handleDelete = (task) => {
    setSelectedTask(task);
    setDeleteDialog(true);
  };

  const handleFormChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleReminderDayChange = (day, checked) => {
    setFormData(prev => ({
      ...prev,
      reminders: {
        ...prev.reminders,
        days: checked
          ? [...prev.reminders.days, day]
          : prev.reminders.days.filter(d => d !== day),
      },
    }));
  };

  const handleIconFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        toastMessage("Please select a valid image file (PNG, JPG, JPEG, or SVG)", "error");
        return;
      }
      
      // Validate file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        toastMessage("File size must be less than 2MB", "error");
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        iconFile: file,
        iconUrl: URL.createObjectURL(file) // For preview
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      // Frontend validation
      if (formData.sessionNo === undefined || formData.sessionNo === null || formData.sessionNo === "") {
        toastMessage("Please select a session number", "error");
        return;
      }
      if (!formData.sessionType || formData.sessionType === "") {
        toastMessage("Please select a session type", "error");
        return;
      }
      if (!formData.title || formData.title.trim() === "") {
        toastMessage("Please enter a title", "error");
        return;
      }
      if (!formData.description || formData.description.trim() === "") {
        toastMessage("Please enter a description", "error");
        return;
      }

      let payload = {
        ...formData,
        sessionNo: parseInt(formData.sessionNo),
        dueInterval: parseInt(formData.dueInterval),
        order: parseInt(formData.order),
      };

      // Handle icon upload if a new file is selected
      if (formData.iconFile) {
        try {
          console.log("Uploading icon file:", formData.iconFile);
          const form = new FormData();
          form.append("file", formData.iconFile);
          console.log("FormData created, sending request...");
          const response = await postFiles("/api/app/user/uploadImage", form);
          console.log("Upload response:", response);
          
          if (response.data && response.data.url) {
            payload.icon = response.data.url;
            console.log("Icon URL set:", payload.icon);
          } else {
            throw new Error("No URL returned from upload");
          }
        } catch (error) {
          console.error("Error uploading icon:", error);
          toastMessage(`Error uploading icon: ${error.message || "Unknown error"}`, "error");
          return;
        }
      }

      // Remove iconFile from payload as it's not part of the model
      delete payload.iconFile;

      if (isEditing) {
        await put(`/api/dashboard/cbt/cbt-tasks/${selectedTask._id}`, payload);
        toastMessage("Task updated successfully", "success");
      } else {
        await post("/api/dashboard/cbt/cbt-tasks", payload);
        toastMessage("Task created successfully", "success");
      }

      setTaskDialog(false);
      fetchTasks();
    } catch (error) {
      console.error("Error saving task:", error);
      toastMessage(error.response?.data?.message || "Error saving task", "error");
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await del(`/api/dashboard/cbt/cbt-tasks/${selectedTask._id}`);
      toastMessage("Task deleted successfully", "success");
      setDeleteDialog(false);
      fetchTasks();
    } catch (error) {
      console.error("Error deleting task:", error);
      toastMessage(error.response?.data?.message || "Error deleting task", "error");
    }
  };

  const getSessionTypeColor = (sessionType) => {
    const colors = {
      DISCOVERY: "primary",
      ANALYSIS: "secondary",
      SOLUTION: "success",
    };
    return colors[sessionType] || "default";
  };

  const getFrequencyColor = (frequency) => {
    const colors = {
      "One-time": "default",
      Daily: "primary",
      Weekly: "secondary",
      Monthly: "success",
    };
    return colors[frequency] || "default";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              CBT Tasks
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage master CBT tasks for each session
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Add Task
          </Button>
        </Box>

        {/* Search Filter */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Search tasks by title or description"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  onClick={fetchTasks}
                  fullWidth
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tasks Table - Grouped by Session */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography>Loading...</Typography>
              </Box>
            ) : tasks.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography>No tasks found</Typography>
              </Box>
            ) : (
              <>
                {/* Summary Stats */}
                <Box sx={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center", 
                  mb: 3, 
                  p: 2, 
                  bgcolor: "grey.100", 
                  borderRadius: 1
                }}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Total Tasks: {tasks.length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Across {new Set(tasks.map(t => t.sessionNo)).size} sessions
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const allSessions = new Set(tasks.map(t => t.sessionNo));
                        const allExpanded = {};
                        allSessions.forEach(session => {
                          allExpanded[session] = true;
                        });
                        setExpandedSessions(allExpanded);
                      }}
                    >
                      Expand All
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setExpandedSessions({})}
                    >
                      Collapse All
                    </Button>
                  </Box>
                </Box>

                {/* Group tasks by session number */}
                {(() => {
                  const groupedTasks = tasks.reduce((groups, task) => {
                    const sessionNo = task.sessionNo;
                    if (!groups[sessionNo]) {
                      groups[sessionNo] = [];
                    }
                    groups[sessionNo].push(task);
                    return groups;
                  }, {});

                  // Sort sessions and tasks within each session
                  const sortedSessions = Object.keys(groupedTasks).sort((a, b) => parseInt(a) - parseInt(b));
                  
                  return sortedSessions.map((sessionNo) => {
                    const isExpanded = expandedSessions[sessionNo];
                    const sessionTasks = groupedTasks[sessionNo];
                    
                    return (
                      <Box key={sessionNo} sx={{ mb: 2 }}>
                        {/* Collapsible Session Header */}
                        <Box 
                          sx={{ 
                            display: "flex", 
                            alignItems: "center", 
                            justifyContent: "space-between",
                            p: 2, 
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            cursor: "pointer",
                            bgcolor: "white",
                            "&:hover": {
                              bgcolor: "#f5f5f5"
                            }
                          }}
                          onClick={() => setExpandedSessions(prev => ({
                            ...prev,
                            [sessionNo]: !prev[sessionNo]
                          }))}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="h6" fontWeight="bold" color="text.primary">
                              Session {sessionNo}
                            </Typography>
                            <Typography variant="body2" sx={{ ml: 2, color: "text.secondary" }}>
                              {sessionTasks.length} task{sessionTasks.length !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                          <IconButton 
                            size="small" 
                            sx={{ color: "text.primary" }}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Box>

                        {/* Collapsible Tasks for this session */}
                        {isExpanded && (
                          <Box sx={{ mt: 1, ml: 2 }}>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Session Type</TableCell>
                                    <TableCell>Frequency</TableCell>
                                    <TableCell>Due Interval</TableCell>
                                    <TableCell>Reminders</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {sessionTasks
                                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                                    .map((task) => (
                                    <TableRow key={task._id}>
                                      <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                          {task.icon && (
                                            <Avatar
                                              src={task.icon}
                                              sx={{ width: 32, height: 32, mr: 1 }}
                                              onError={(e) => {
                                                console.error("Avatar image load error:", e);
                                                console.error("Failed to load image:", task.icon);
                                              }}
                                              onLoad={() => {
                                                console.log("Avatar image loaded successfully:", task.icon);
                                              }}
                                            />
                                          )}
                                          {!task.icon && (
                                            <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: "grey.300" }}>
                                              <Typography variant="caption">No Icon</Typography>
                                            </Avatar>
                                          )}
                                          <Typography variant="body2" fontWeight="bold">
                                            {task.title}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                          {task.description}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={task.sessionType}
                                          color={getSessionTypeColor(task.sessionType)}
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={task.frequency}
                                          color={getFrequencyColor(task.frequency)}
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">
                                          {task.dueInterval} day{task.dueInterval !== 1 ? "s" : ""}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={task.reminders?.enabled ? "Enabled" : "Disabled"}
                                          color={task.reminders?.enabled ? "success" : "default"}
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">
                                          {formatDate(task.createdAt)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                          <Tooltip title="Edit">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleEdit(task)}
                                            >
                                              <EditIcon />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Delete">
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => handleDelete(task)}
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </Box>
                    );
                  });
                })()}
              </>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Task Dialog */}
        <Dialog
          open={taskDialog}
          onClose={() => setTaskDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {isEditing ? "Edit CBT Task" : "Add New CBT Task"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Fields marked with * are required
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Session Number *</InputLabel>
                                     <Select
                     value={formData.sessionNo}
                     onChange={(e) => handleFormChange("sessionNo", e.target.value)}
                     label="Session Number *"
                     error={formData.sessionNo === undefined || formData.sessionNo === null || formData.sessionNo === ""}
                   >
                    {Array.from({ length: 13 }, (_, i) => (
                      <MenuItem key={i} value={i}>
                        Session {i}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Session Type *</InputLabel>
                  <Select
                    value={formData.sessionType}
                    onChange={(e) => handleFormChange("sessionType", e.target.value)}
                    label="Session Type *"
                    error={!formData.sessionType}
                  >
                    {sessionTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title *"
                  value={formData.title}
                  onChange={(e) => handleFormChange("title", e.target.value)}
                  inputProps={{ maxLength: 60 }}
                  helperText={`${formData.title.length}/60 characters`}
                  required
                  error={!formData.title.trim()}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description *"
                  value={formData.description}
                  onChange={(e) => handleFormChange("description", e.target.value)}
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 200 }}
                  helperText={`${formData.description.length}/200 characters`}
                  required
                  error={!formData.description.trim()}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Task Icon
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleIconFileChange}
                    style={{ display: 'none' }}
                    id="icon-file-input"
                  />
                  <label htmlFor="icon-file-input">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      fullWidth
                      sx={{ mb: 1 }}
                    >
                      {formData.iconFile ? formData.iconFile.name : (formData.icon ? 'Change Icon' : 'Upload Icon')}
                    </Button>
                  </label>
                  {formData.icon && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={formData.icon}
                        sx={{ width: 40, height: 40 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Icon preview
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={formData.frequency}
                    onChange={(e) => handleFormChange("frequency", e.target.value)}
                    label="Frequency"
                  >
                    {frequencies.map((freq) => (
                      <MenuItem key={freq} value={freq}>
                        {freq}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Due Interval (days)"
                  type="number"
                  value={formData.dueInterval}
                  onChange={(e) => handleFormChange("dueInterval", e.target.value)}
                  inputProps={{ min: 1, max: 365 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => handleFormChange("order", e.target.value)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.reminders.enabled}
                      onChange={(e) => handleFormChange("reminders.enabled", e.target.checked)}
                    />
                  }
                  label="Enable Reminders"
                />
              </Grid>
              {formData.reminders.enabled && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Reminder Time"
                      type="time"
                      value={formData.reminders.time}
                      onChange={(e) => handleFormChange("reminders.time", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <Typography variant="subtitle2" gutterBottom>
                        Reminder Days
                      </Typography>
                      <FormGroup row>
                        {daysOfWeek.map((day) => (
                          <FormControlLabel
                            key={day.value}
                            control={
                              <Checkbox
                                checked={formData.reminders.days.includes(day.value)}
                                onChange={(e) => handleReminderDayChange(day.value, e.target.checked)}
                              />
                            }
                            label={day.label}
                          />
                        ))}
                      </FormGroup>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {isEditing ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog}
          onClose={() => setDeleteDialog(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the task "{selectedTask?.title}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default CbtTasks; 